// Archivo que maneja el administrador de layouts por producto
import { useParams, useNavigate } from 'react-router-dom'
import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  Snackbar,
  Typography,
} from '@mui/material'
import PreviewIcon from '@mui/icons-material/Preview'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar'
import api from '../../services/api'
import { getLayoutsByProduct } from '../../services/layoutService'
import {
  getOperationalProductsByClient,
  getSessionUserNoCache,
} from '../../services/operationalContextService'
import {
  AdminHero,
  AdminSectionCard,
} from '../../components/settings/AdminConsoleShell'

const isGlobalAdminRole = (roles) => {
  if (!Array.isArray(roles)) return false
  return roles.some((r) => {
    const type = typeof r === 'string' ? r : r?.type
    const name = typeof r === 'string' ? r : r?.name_role
    return type === 'globalAdmin' || name === 'globalAdmin'
  })
}

const ProductLayoutManager = () => {
  const navigate = useNavigate()
  const { productId } = useParams()

  const [meUser, setMeUser] = useState(null)
  const [loadingMe, setLoadingMe] = useState(false)

  const [products, setProducts] = useState([])
  const [loadingProducts, setLoadingProducts] = useState(false)

  const [layouts, setLayouts] = useState([])
  const [loadingLayouts, setLoadingLayouts] = useState(false)

  const [selectedLayout, setSelectedLayout] = useState('')
  const [selectedProducts, setSelectedProducts] = useState([])

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })

  const isGlobalAdmin = useMemo(
    () => isGlobalAdminRole(meUser?.roles),
    [meUser]
  )

  const safeProducts = useMemo(
    () => (Array.isArray(products) ? products : []),
    [products]
  )

  const safeLayouts = useMemo(
    () => (Array.isArray(layouts) ? layouts : []),
    [layouts]
  )

  const hasOperationalContext = useMemo(() => {
    if (!isGlobalAdmin) return true
    return Boolean(meUser?.clientId && meUser?.productId)
  }, [isGlobalAdmin, meUser])

  const anchorProductId = useMemo(() => {
    const parsedRouteProductId = Number(productId)
    const parsedOperationalProductId = Number(meUser?.productId)

    if (Number.isInteger(parsedRouteProductId) && parsedRouteProductId > 0) {
      return parsedRouteProductId
    }

    return Number.isInteger(parsedOperationalProductId) &&
      parsedOperationalProductId > 0
      ? parsedOperationalProductId
      : null
  }, [productId, meUser?.productId])

  const targetProductIds = selectedProducts

  useEffect(() => {
    const loadMe = async () => {
      setLoadingMe(true)
      try {
        const user = await getSessionUserNoCache()
        setMeUser(user || null)
      } catch (err) {
        setMeUser(null)
        setSnackbar({
          open: true,
          message: 'No se pudo obtener la sesión (auth/me).',
          severity: 'error',
        })
      } finally {
        setLoadingMe(false)
      }
    }

    loadMe()
  }, [])

  useEffect(() => {
    const loadProducts = async () => {
      if (!meUser) return

      setLoadingProducts(true)
      try {
        if (isGlobalAdmin) {
          if (!meUser.clientId) {
            setProducts([])
            setSelectedProducts([])
            return
          }

          const list = await getOperationalProductsByClient(meUser.clientId)
          setProducts(list)

          if (productId && list.length > 0) {
            const match = list.find((p) => String(p.id) === String(productId))
            if (match) {
              setSelectedProducts([match.id])
            } else if (meUser?.productId) {
              setSelectedProducts([meUser.productId])
            } else {
              setSelectedProducts([])
            }
          } else if (meUser?.productId) {
            const exists = list.some(
              (product) => String(product.id) === String(meUser.productId)
            )
            setSelectedProducts(exists ? [meUser.productId] : [])
          } else {
            setSelectedProducts([])
          }

          return
        }

        const res = await api.get('/products')
        const data = res?.data
        const list = Array.isArray(data) ? data : data?.products || []
        const normalizedList = Array.isArray(list) ? list : []

        setProducts(normalizedList)

        if (productId && normalizedList.length > 0) {
          const match = normalizedList.find(
            (p) => String(p.id) === String(productId)
          )
          if (match) {
            setSelectedProducts([match.id])
          }
        }
      } catch (err) {
        setProducts([])
        setSelectedProducts([])
        setSnackbar({
          open: true,
          message: 'Error al cargar productos.',
          severity: 'error',
        })
      } finally {
        setLoadingProducts(false)
      }
    }

    loadProducts()
  }, [meUser, isGlobalAdmin, productId])

  useEffect(() => {
    const loadLayouts = async () => {
      if (!meUser) return

      if (isGlobalAdmin && !hasOperationalContext) {
        setLayouts([])
        setSelectedLayout('')
        return
      }

      if (!anchorProductId) {
        setLayouts([])
        setSelectedLayout('')
        return
      }

      setLoadingLayouts(true)
      try {
        const layoutsData = await getLayoutsByProduct(anchorProductId)
        const payload = layoutsData?.data ?? layoutsData
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.layouts)
            ? payload.layouts
            : payload
              ? [payload]
              : []

        setLayouts(list)

        setSelectedLayout((prev) => {
          if (!prev) return ''
          const stillExists = list.some((l) => String(l.id) === String(prev))
          return stillExists ? prev : ''
        })
      } catch (err) {
        setLayouts([])
        setSelectedLayout('')
        setSnackbar({
          open: true,
          message: 'Error al cargar layouts.',
          severity: 'error',
        })
      } finally {
        setLoadingLayouts(false)
      }
    }

    loadLayouts()
  }, [
    meUser,
    isGlobalAdmin,
    hasOperationalContext,
    anchorProductId,
  ])

  const handleApplyLayout = async () => {
    if (!selectedLayout || targetProductIds.length === 0) return

    try {
      await api.patch(`/layouts/${selectedLayout}/apply`, {
        productIds: targetProductIds,
      })

      setSnackbar({
        open: true,
        message: '¡Layout aplicado exitosamente!',
        severity: 'success',
      })
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error al aplicar el layout.',
        severity: 'error',
      })
    }
  }

  const handlePreview = () => {
    if (!selectedLayout) return

    const selectedProduct = safeProducts.find(
      (p) => String(p.id) === String(anchorProductId)
    )

    navigate(`/settings/controlpanel/preview/${selectedLayout}`, {
      state: { selectedProduct },
    })
  }

  const loadingAny = loadingMe || loadingProducts || loadingLayouts

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <AdminHero
        eyebrow="Layout administration"
        title="Administrador de Layouts"
        description="Consola operativa de layouts que consume el contexto operativo ya activado desde su vista oficial."
        stats={[
          { label: 'Layouts disponibles', value: safeLayouts.length },
          { label: 'Productos en contexto', value: safeProducts.length },
          { label: 'Productos seleccionados', value: selectedProducts.length },
        ]}
        actions={
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <Button
              variant="contained"
              onClick={() => {
                if (!anchorProductId) return
                navigate(
                  `/settings/controlpanel/products/${anchorProductId}/layout-editor`
                )
              }}
              startIcon={<ViewSidebarIcon />}
            >
              Abrir builder
            </Button>
            <Button
              variant="outlined"
              onClick={handlePreview}
              disabled={!selectedLayout}
              startIcon={<PreviewIcon />}
            >
              Vista previa
            </Button>
          </Stack>
        }
      />

      {isGlobalAdmin && (
        <AdminSectionCard
          title="Contexto operativo (solo lectura)"
          subtitle="La activación oficial de cliente/producto se realiza en /settings/controlpanel/operational-context."
        >
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Contexto activo actual: clientId {meUser?.clientId || '—'} /
              productId {meUser?.productId || '—'}.
            </Typography>

            {!hasOperationalContext && (
              <Alert
                severity="warning"
                action={
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() =>
                      navigate('/settings/controlpanel/operational-context')
                    }
                  >
                    Definir contexto
                  </Button>
                }
              >
                Tu sesión globalAdmin no tiene contexto operativo activo. Debes
                activarlo antes de operar layouts sensibles.
              </Alert>
            )}
          </Stack>
        </AdminSectionCard>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={5}>
          <AdminSectionCard
            title="Selección de layout"
            subtitle="Visualiza el catálogo del contexto actual y dispara preview sin cambiar la navegación existente."
            actions={
              <Button
                variant="outlined"
                onClick={handlePreview}
                disabled={!selectedLayout}
                startIcon={<PreviewIcon />}
              >
                Vista previa
              </Button>
            }
            sx={{ height: '100%' }}
          >
            <Stack spacing={2}>
              {isGlobalAdmin && !hasOperationalContext && (
                <Alert severity="info">
                  Activa primero tu contexto operativo para cargar layouts.
                </Alert>
              )}

              <Select
                fullWidth
                value={selectedLayout}
                onChange={(e) => setSelectedLayout(e.target.value)}
                displayEmpty
                disabled={
                  loadingAny ||
                  safeLayouts.length === 0 ||
                  !hasOperationalContext
                }
              >
                <MenuItem value="">
                  <em>Selecciona un layout</em>
                </MenuItem>
                {safeLayouts.map((layout) => (
                  <MenuItem key={layout.id} value={layout.id}>
                    {layout.name} {layout.version ? `- v${layout.version}` : ''}
                  </MenuItem>
                ))}
              </Select>

              {!loadingLayouts &&
                safeLayouts.length === 0 &&
                hasOperationalContext && (
                  <Alert severity="warning">
                    No hay layouts disponibles para el contexto actual.
                  </Alert>
                )}
            </Stack>
          </AdminSectionCard>
        </Grid>

        <Grid item xs={12} lg={7}>
          <AdminSectionCard
            title="Asignación a productos"
            subtitle="Selecciona uno o varios productos del contexto actual y aplica el layout activo con la misma operación existente."
            actions={
              <Button
                variant="contained"
                color="primary"
                onClick={handleApplyLayout}
                disabled={
                  !selectedLayout ||
                  selectedProducts.length === 0 ||
                  loadingAny ||
                  !hasOperationalContext
                }
                startIcon={<AssignmentTurnedInIcon />}
              >
                Aplicar Layout Seleccionado
              </Button>
            }
            sx={{ height: '100%' }}
          >
            {loadingProducts ? (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={22} />
                <Typography variant="body2">Cargando productos…</Typography>
              </Stack>
            ) : (
              <Grid container spacing={1}>
                {safeProducts.map((product) => (
                  <Grid item xs={12} md={6} key={product.id}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: selectedProducts.includes(product.id)
                          ? 'primary.main'
                          : 'divider',
                        borderRadius: 2,
                        px: 1.5,
                        py: 1,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedProducts.includes(product.id)}
                            onChange={() =>
                              setSelectedProducts((prev) =>
                                prev.includes(product.id)
                                  ? prev.filter((id) => id !== product.id)
                                  : [...prev, product.id]
                              )
                            }
                            disabled={!hasOperationalContext}
                          />
                        }
                        label={
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {product.name_product}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ID: {product.id}
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}

            {!loadingProducts && safeProducts.length === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                No hay productos disponibles para el contexto actual.
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Producto ancla del contexto:{' '}
              {productId || meUser?.productId || 'no especificado'}.
            </Typography>
          </AdminSectionCard>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default ProductLayoutManager
